
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  *
  * The source file is located at:
  *   /codebuild/output/src1738611268/src/vl-app/pages/patients/legal.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
// @ts-check
import React, { useCallback, useEffect, useState } from 'react';
import { Spin, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import { useRouter } from '@hooks/useRouter';
import { css } from '@emotion/react';
import { LoadingOutlined } from 'antd/icons';
import { useQuery } from '@tanstack/react-query';
import breakpoints from '@lib/breakpoints';
import { useTranslation } from 'react-i18next';
import Header from '@components/Layouts/Header/Header';

const DangerouslyFetchRemoteHTML = ({ url }: { url: string | null }) => {
  const [htmlContent, setHtmlContent] = useState('');
  useEffect(() => {
    if (!url) return;
    const fetchHTML = async () => {
      const response = await fetch(url);
      const text = await response.text();
      setHtmlContent(text);
    };
    fetchHTML();
  }, [url]);
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

// NB: now works in two modes controlled by the "all_policies" query parameter. If set, will work regardless of
// whether a user is logged in and simply display all the policies. If not set then assumes that a user is
// logged in and displays those policies that the user has agreed to. I rather suspect that in practice the
// user has to agree to all the policies in order to login in any case, so I think the acceptance test is redundant.
const Legal = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { push, query, pathname } = useRouter();
  const initialised = useInitialisedStore();

  const { data: policiesData = [] } = useQuery({
    queryKey: ['allPatientPolicies'],
    queryFn: async () => {
      const response = await dispatch(actions.allPatientPolicies());
      return response;
    },
    select: (data) => data.policies,
    enabled: !!query.all_policies
  });

  const { data: userPoliciesData = [] } = useQuery({
    queryKey: ['userPolicicies'],
    queryFn: async () => {
      const response = await dispatch(actions.userPolicy('USER_POLICY'));
      return response.payload?.filter((p) => p.accepted) || [];
    },
    enabled: !query.all_policies && initialised
  });

  const policyList = query.all_policies ? policiesData : userPoliciesData;

  const policyTabItems: TabsProps['items'] | undefined =
    policyList && policyList.length > 0
      ? policyList.map((policy, index) => ({
          label: (
            <span
              title={policy.description}
              data-testid={`tab-${policy.description.replace(/\s/g, '-').toLowerCase()}`}
            >
              {policy.description}
            </span>
          ),
          key: policy.policy_code,
          children: (
            <div
              css={css`
                overflow: auto;
                padding: 0 0 1em 0;
              `}
            >
              <DangerouslyFetchRemoteHTML key={`${policy.policy_code}`} url={policy.url} />
            </div>
          )
        }))
      : undefined;

  const changeActiveTab = useCallback(
    (newTab: string) => {
      const newQuery = { ...query, activeTab: newTab };
      push({ pathname, query: newQuery }, undefined, { shallow: true });
    },
    [pathname, push, query]
  );

  const defaultActiveKey = query.activeTab as string;

  if (policyTabItems?.length === 0 || !policyTabItems) {
    return <p>{t('Initialising ...')}</p>;
  }

  return (
    <>
      <Header pageType={query.all_policies ? 'login' : 'patients'} pageTitle="Legal" />
      <section className="section-layout">
        <main
          className="container"
          id="page-content"
          css={css`
            max-width: ${breakpoints.xl}px;
            margin: 0 auto;
          `}
        >
          {policyTabItems && policyTabItems?.length > 0 ? (
            <div
              css={css`
                background: #fff;
                min-height: max-content;
                .ant-tabs-tab {
                  width: 100%;
                }
                .ant-tabs-tab-btn {
                  text-overflow: ellipsis;
                  overflow: hidden;
                }
              `}
            >
              <Tabs defaultActiveKey={defaultActiveKey} items={policyTabItems} onChange={changeActiveTab} />
            </div>
          ) : (
            <Spin
              style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              indicator={<LoadingOutlined style={{ fontSize: 26 }} spin />}
            />
          )}
        </main>
      </section>
    </>
  );
};


export const Route = createFileRoute('/patients/legal')({ component: Legal });
